import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { SelectPicker } from 'rsuite';
import { getFlow, getFlowActiveYear, getFlowByYear } from '../functions';
import 'rsuite/dist/rsuite.min.css';
import * as echarts from 'echarts';
import SectionTitle from "./SectionTitle";
import MultiLingualContent from "../config/multilingualContent";
import { LanguageContext } from "../config/multilingualContext";
import Loading from './Loading';
import '../assets/css/sigmap.css';
import '../assets/css/map.css';
import { formatNumber } from './FormattedNumber';

const GEO_COORDS = {
    Nigeria:        [8.67,   9.06],
    Ghana:          [-0.20,  7.97],
    'Ivory Coast':  [-5.55,  7.54],
    Senegal:        [-17.47, 14.73],
    Mali:           [-8.00,  12.65],
    'Burkina Faso': [-1.52,  12.37],
    Guinea:         [-13.71, 9.95],
    Benin:          [2.61,   6.48],
    Niger:          [2.11,   13.51],
    Chad:           [18.73,  15.45],
    Togo:           [1.22,   6.14],
    'Sierra Leone': [-13.29, 8.48],
    Liberia:        [-10.80, 6.33],
    Gambia:         [-16.65, 13.45],
    'Guinea-Bissau':[-15.60, 11.86],
    'Cape Verde':   [-23.62, 15.08],
};

// Mapping code ISO 3 lettres → nom carte ECharts
const CODE_TO_MAP = {
    NGA: 'Nigeria',
    GHA: 'Ghana',
    CIV: 'Ivory Coast',
    SEN: 'Senegal',
    MLI: 'Mali',
    BFA: 'Burkina Faso',
    GIN: 'Guinea',
    BEN: 'Benin',
    NER: 'Niger',
    TCD: 'Chad',
    TGO: 'Togo',
    SLE: 'Sierra Leone',
    LBR: 'Liberia',
    GMB: 'Gambia',
    GNB: 'Guinea-Bissau',
    CPV: 'Cape Verde',
};

const COLORS = [
    '#e74c3c','#2ecc71','#3498db','#f39c12','#9b59b6',
    '#1abc9c','#e67e22','#34495e','#d35400','#27ae60',
    '#2980b9','#8e44ad','#c0392b','#16a085','#7f8c8d','#95a5a6'
];

const COUNTRY_NAMES = Object.keys(GEO_COORDS);

// Normalise via le code ISO
const normalizeCountry = (code) =>
    CODE_TO_MAP[(code || '').toUpperCase().trim()] || null;

const getColor = (country) => {
    const idx = COUNTRY_NAMES.indexOf(country);
    return COLORS[idx >= 0 ? idx : COLORS.length - 1];
};

const GEO_OPTION = {
    map: 'cedeao', roam: true,
    layoutCenter: ['50%', '50%'], layoutSize: '100%', zoom: 1.1,
    label: { show: true, fontSize: 10 },
    itemStyle: { areaColor: '#f5f7fa', borderColor: '#ccc' },
    emphasis: { itemStyle: { areaColor: '#5470c6' } }
};

const MapItem = () => {
    const { language } = useContext(LanguageContext);

    const chartRef     = useRef(null);
    const containerRef = useRef(null);
    const worldGeoRef  = useRef(null);

    const [flows,          setFlows]          = useState([]);
    const [activeFlowYear, setActiveFlowYear] = useState([]);
    const [indicator,      setIndicator]      = useState(null);
    const [year,           setYear]           = useState(null);
    const [mapReady,       setMapReady]       = useState(false);
    const [payload,        setPayload]        = useState({ baseFlows: [], countryHaveData: [] });
    const [hasData,        setHasData]        = useState(false);
    const [loadingData,    setLoadingData]    = useState(false);

    const getLabel = (item) => ({
        french:     item?.label_fr ?? '',
        english:    item?.label_en ?? '',
        portuguais: item?.label_pr ?? '',
        swahili:    item?.label_sw ?? '',
    })[language] || '';

    // countryHaveData = ["BEN", "NGA", ...] — strings de codes ISO
    const visibleCountries = useMemo(() =>
        (payload.countryHaveData || [])
            .map(code => normalizeCountry(code))
            .filter(name => name && GEO_COORDS[name]),
        [payload.countryHaveData]
    );

    // Fetch liste des flux + années actives
    useEffect(() => {
        Promise.all([
            getFlow(),
            getFlowActiveYear()
        ])
        .then(([flowResponse, yearResponse]) => {
            const list  = Array.isArray(flowResponse?.flows) ? flowResponse.flows : [];
            const years = Array.isArray(yearResponse?.years) ? yearResponse.years : [];

            setFlows(list);
            if (list.length) setIndicator(list[0].id);

            setActiveFlowYear(years);
            if (years.length) setYear(String(years[years.length - 1]));
        })
        .catch(() => {
            setFlows([]);
            setActiveFlowYear([]);
        });
    }, []);

    // Fetch données par indicateur + année
    useEffect(() => {
        if (!indicator || !year) return;

        setLoadingData(true);
        setPayload({ baseFlows: [], countryHaveData: [] });
        setHasData(false);

        getFlowByYear(indicator, year)
            .then((response) => {
                const baseFlows       = Array.isArray(response?.baseFlows)       ? response.baseFlows       : [];
                const countryHaveData = Array.isArray(response?.countryHaveData) ? response.countryHaveData : [];

                setPayload({ baseFlows, countryHaveData });
                setHasData(baseFlows.length > 0 || countryHaveData.length > 0);
            })
            .catch(() => {
                setPayload({ baseFlows: [], countryHaveData: [] });
                setHasData(false);
            })
            .finally(() => setLoadingData(false));
    }, [indicator, year]);

    // Init carte ECharts — une seule fois
    useEffect(() => {
        let mounted = true;

        fetch("https://fastly.jsdelivr.net/npm/echarts@4.9.0/map/json/world.json")
            .then(r => r.json())
            .then(world => {
                if (!mounted) return;

                worldGeoRef.current = world;

                const geoJSON = {
                    ...world,
                    features: world.features.filter(f =>
                        COUNTRY_NAMES.includes(f.properties.name)
                    )
                };

                echarts.registerMap('cedeao', geoJSON);

                const chart = echarts.init(containerRef.current);
                chartRef.current = chart;

                chart.setOption({
                    geo: GEO_OPTION,
                    series: []
                });

                window.addEventListener('resize', () => chart.resize());
                setMapReady(true);
            })
            .catch(console.error);

        return () => {
            mounted = false;
            chartRef.current?.dispose();
            chartRef.current = null;
        };
    }, []);

    // Re-filtre la carte GeoJSON selon les pays ayant des données
    useEffect(() => {
        if (!mapReady || !chartRef.current || !worldGeoRef.current) return;

        if (!visibleCountries.length) {
            chartRef.current.setOption({
                geo: GEO_OPTION,
                series: []
            });
            return;
        }

        const filteredGeoJSON = {
            ...worldGeoRef.current,
            features: worldGeoRef.current.features.filter(f =>
                visibleCountries.includes(f.properties.name)
            )
        };

        echarts.registerMap('cedeao', filteredGeoJSON);
        chartRef.current.setOption({ geo: GEO_OPTION });

    }, [mapReady, visibleCountries]);

    // Mise à jour des données sur la carte
    useEffect(() => {
        if (!mapReady || !chartRef.current) return;

        const chart      = chartRef.current;
        const visibleSet = new Set(visibleCountries);

        if (!hasData || !visibleCountries.length) {
            chart.setOption({
                geo: GEO_OPTION,
                series: []
            });
            return;
        }

        const normalizedFlows = (payload.baseFlows || [])
            .map(f => {
                // from et to sont des objets pays — utilise le code pour normaliser
                const fromName = normalizeCountry(f.from?.code ?? '');
                const toName   = normalizeCountry(f.to?.code   ?? '');

                return {
                    from:      fromName,
                    to:        toName,
                    fromLabel: f.from ? getLabel(f.from) : '',  // label dans la langue du site
                    toLabel:   f.to   ? getLabel(f.to)   : '',  // label dans la langue du site
                    value:     Number(f.value) || 0,
                    unit:      f.unit ? getLabel(f.unit) : '',  // unit est un objet avec label_fr/en...
                };
            })
            .filter(f => f.from && f.to && visibleSet.has(f.from) && visibleSet.has(f.to));

        // FIX 1: agréger les doublons par paire from/to
        const aggregatedFlows = normalizedFlows.reduce((acc, f) => {
            const key = `${f.from}__${f.to}`;
            if (!acc[key]) {
                acc[key] = { ...f };
            } else {
                acc[key].value += f.value;
            }
            return acc;
        }, {});
        const cleanFlows = Object.values(aggregatedFlows)
            // FIX 2: valeur absolue pour les négatifs, filtrer les zéros
            .map(f => ({ ...f, value: Math.abs(f.value) }))
            .filter(f => f.value > 0);

        const allValues = cleanFlows.map(f => f.value);
        const maxValue  = Math.max(...allValues, 1);

        const allTotals = visibleCountries.map(name => {
            const cf = cleanFlows.filter(f => f.from === name || f.to === name);
            return cf.reduce((s, f) => s + f.value, 0);
        });
        const maxTotal = Math.max(...allTotals, 1);


        const linesData = cleanFlows
            .filter(f => GEO_COORDS[f.from] && GEO_COORDS[f.to])
            .map(f => ({
                coords:    [GEO_COORDS[f.from], GEO_COORDS[f.to]],
                fromName:  f.fromLabel,
                toName:    f.toLabel,
                value:     f.value,
                lineStyle: {
                    // FIX: largeur normalisée entre 1 et 8px
                    width:     1 + (f.value / maxValue) * 3,
                    color:     getColor(f.from),
                    curveness: 0.25,
                },
            }));

        const scatterData = visibleCountries.map(name => {
            const countryFlows = cleanFlows.filter(f => f.from === name || f.to === name); // ← était normalizedFlows
            //const countryFlows = normalizedFlows.filter(f => f.from === name || f.to === name);
            const exports      = countryFlows.filter(f => f.from === name).reduce((s, f) => s + f.value, 0);
            const imports      = countryFlows.filter(f => f.to   === name).reduce((s, f) => s + f.value, 0);
            const unit         = countryFlows.find(f => f.unit)?.unit || '';

            console.log(countryFlows);
            // Label localisé du pays selon la langue
            const countryLabel = countryFlows.find(f => f.from === name)?.fromLabel
                              || countryFlows.find(f => f.to   === name)?.toLabel
                              || name;

            return {
                name,
                label:   countryLabel,
                value:   [...GEO_COORDS[name], exports + imports],
                exports, imports,
                total:   exports + imports,
                unit,
            };
        });


        chart.setOption({
            symbolSize: val => 8 + (val[2] / maxTotal) * 32,
            tooltip: {
                trigger: 'item',
                formatter: params => {
                    if (params.seriesType === 'scatter') {
                        const { label, total, unit, exports, imports } = params.data;
                        return `
                            <strong>${label}</strong><br/>
                            Total: ${formatNumber(total, language, 2)}${unit ? ` ${unit}` : ''}<br/>
                            Exports: ${formatNumber(exports, language, 2)}${unit ? ` ${unit}` : ''}<br/>
                            Imports: ${formatNumber(imports, language, 2)}${unit ? ` ${unit}` : ''}
                        `;
                    }
                    return '';
                }
            },
            geo: GEO_OPTION,
            series: [
                {
                    type: 'lines',
                    coordinateSystem: 'geo',
                    effect: {
                        show: true, symbol: 'arrow',
                        symbolSize: 5, trailLength: 0.4, period: 4
                    },
                    lineStyle: { opacity: 0.8 },
                    data: linesData,
                },
                {
                    type: 'scatter',
                    coordinateSystem: 'geo',
                    // FIX: taille fixe en pixels, indépendante des valeurs
                    symbolSize: val => {
                        const normalized = val[2] / maxTotal; // 0 à 1
                        return Math.round(8 + normalized * 24); // entre 8px et 32px max
                    },
                    symbol: 'circle',
                    label: {
                        show: true,
                        position: 'right',
                        fontSize: 11,
                        formatter: p => `${p.data.label}\n${formatNumber(p.data.total, language, 2)}${p.data.unit ? ` ${p.data.unit}` : ''}`,
                    },
                    itemStyle: { 
                        color: p => getColor(p.data.name), 
                        opacity: 0.7,  // FIX: réduit l'opacité pour moins écraser la carte
                    },
                    emphasis: { scale: true },
                    data: scatterData,
                },
            ],
        });

    }, [indicator, year, mapReady, visibleCountries, payload, hasData, language]);

    return (
        <div className="container about-list-items mb-5">
            <SectionTitle pageTitle="map" />

            <div className="row mt-2">
                <div className="col-md-4 mt-2">
                    <SelectPicker
                        data={flows.map(i => ({ label: getLabel(i), value: i.id }))}
                        value={indicator}
                        onChange={val => val && setIndicator(val)}
                        cleanable={false} searchable={false}
                        style={{ width: '100%' }}
                    />
                </div>
                <div className="col-md-4 mt-2">
                    <SelectPicker
                        data={activeFlowYear.map(y => ({ label: String(y), value: String(y) }))}
                        value={year}
                        onChange={val => val && setYear(val)}
                        cleanable={false} searchable={false}
                        style={{ width: '100%' }}
                    />
                </div>
            </div>

            <div className="map-card card mt-4">
                <div className="card-body" style={{ height: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>

                    {/* Loading overlay */}
                    {loadingData && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.8)' }}>
                            <Loading />
                        </div>
                    )}

                    {/* Message pas de données */}
                    {!loadingData && !hasData && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <p className="text-muted">
                                <MultiLingualContent contentID="nodata" />
                            </p>
                        </div>
                    )}

                    {/* Container carte — toujours dans le DOM */}
                    <div
                        ref={containerRef}
                        style={{
                            height: '100%',
                            width: '100%',
                            visibility: (!loadingData && hasData) ? 'visible' : 'hidden'
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default MapItem;